import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  IconButton,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useSnackbar } from '../../components/SnackbarProvider';
import { useLocations } from '../../hooks/useUserLocations';
import axiosInstance from '../../lib/axios';
import type { CashDrawerShift, OpenShiftPayload } from '../../types/cashDrawer';

interface OpenShiftModalProps {
  open: boolean;
  onClose: () => void;
  defaultLocationId?: string;
  // Display name for defaultLocationId. When defaultLocationId is provided (e.g. from the POS
  // header, where the active location is already fixed), the location is shown as a locked field
  // instead of a browsable dropdown — GET /locations requires the 'locations' permission, which
  // store_staff does not have (only store_manager+), so store_staff opening a shift from POS must
  // not depend on that endpoint at all.
  locationName?: string;
}

interface FormData {
  locationId: string;
  openingBalance: number;
}

const defaultValues: FormData = {
  locationId: '',
  openingBalance: 0,
};

export const OpenShiftModal = ({
  open,
  onClose,
  defaultLocationId,
  locationName,
}: OpenShiftModalProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { showMessage } = useSnackbar();
  // Only the general (no fixed location) flow needs the org-wide location list.
  const { data: locations } = useLocations({ enabled: !defaultLocationId });

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({ defaultValues });

  useEffect(() => {
    if (open) {
      reset({ ...defaultValues, locationId: defaultLocationId || '' });
    }
  }, [open, defaultLocationId, reset]);

  const openShiftMutation = useMutation({
    mutationFn: async (data: OpenShiftPayload): Promise<CashDrawerShift> => {
      const response = await axiosInstance.post('/cash-drawer/shifts', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cash-drawer-shifts'] });
      queryClient.invalidateQueries({ queryKey: ['cash-drawer-current-shift'] });
      showMessage(t('cashDrawer.openShiftModal.success'), 'success');
      onClose();
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || t('cashDrawer.openShiftModal.error');
      showMessage(message, 'error');
    },
  });

  const onSubmit = async (data: FormData) => {
    await openShiftMutation.mutateAsync({
      locationId: data.locationId,
      openingBalance: Number(data.openingBalance),
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">{t('cashDrawer.openShiftModal.title')}</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent dividers>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              {defaultLocationId ? (
                <TextField
                  label={t('cashDrawer.openShiftModal.location')}
                  value={locationName || defaultLocationId}
                  fullWidth
                  disabled
                />
              ) : (
                <Controller
                  name="locationId"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <FormControl fullWidth required error={!!errors.locationId}>
                      <InputLabel>{t('cashDrawer.openShiftModal.location')}</InputLabel>
                      <Select
                        {...field}
                        label={t('cashDrawer.openShiftModal.location')}
                        disabled={isSubmitting}
                      >
                        {locations?.map(location => (
                          <MenuItem key={location.id} value={location.id}>
                            {location.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              )}
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="openingBalance"
                control={control}
                rules={{ required: true, min: 0 }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    type="number"
                    label={t('cashDrawer.openingBalance')}
                    fullWidth
                    required
                    inputProps={{ min: 0, step: '0.01' }}
                    error={!!errors.openingBalance}
                    helperText={errors.openingBalance ? t('cashDrawer.openShiftModal.balanceRequired') : ''}
                    disabled={isSubmitting}
                  />
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onClose} disabled={isSubmitting}>
            {t('cashDrawer.cancel')}
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={16} /> : null}
          >
            {t('cashDrawer.openShiftModal.submit')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default OpenShiftModal;
